import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getDepartments } from '../../services/departmentService';
import {
  getEmployeeDetails,
  getEmployees,
} from '../../services/employeeService';
import type { EmployeeDetails, EmployeeListItem } from '../../types/employee';
import { getLocations } from '../../services/locationService';
import {
  createTeam,
  getTeamDetails,
  updateTeam,
} from '../../services/teamService';
import type { TeamDetails } from '../../types/team';
import { activeConfigurationFilters } from '../../utils/configurationFilters';
import {
  isConcurrencyConflict,
  organizationErrorMessage,
} from '../../utils/organizationErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type CreateProps = NativeStackScreenProps<RootStackParamList, 'TeamCreate'>;
type EditProps = NativeStackScreenProps<RootStackParamList, 'TeamEdit'>;
type Props = CreateProps | EditProps;

type FormState = {
  teamCode: string;
  teamName: string;
  description: string;
  departmentId: number | null;
  departmentLabel: string | null;
  locationId: number | null;
  locationLabel: string | null;
  teamLeaderId: number | null;
  teamLeaderLabel: string | null;
  isActive: boolean;
};

const EMPTY: FormState = {
  teamCode: '',
  teamName: '',
  description: '',
  departmentId: null,
  departmentLabel: null,
  locationId: null,
  locationLabel: null,
  teamLeaderId: null,
  teamLeaderLabel: null,
  isActive: true,
};

function looksLikeEmployeeNumber(
  value: string,
  employeeNumber: string,
): boolean {
  return value.trim().toLowerCase() === employeeNumber.trim().toLowerCase();
}

function nameFromEmployeeDetails(details: EmployeeDetails): string | null {
  const joined = [details.firstName, details.middleName, details.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  for (const candidate of [details.displayName, joined]) {
    const name = candidate.trim();
    if (name && !looksLikeEmployeeNumber(name, details.employeeNumber)) {
      return name;
    }
  }
  return null;
}

async function teamLeadLabel(item: EmployeeListItem): Promise<string | null> {
  const listed = item.displayName.trim();
  if (listed && !looksLikeEmployeeNumber(listed, item.employeeNumber)) {
    return listed;
  }
  try {
    const details = await getEmployeeDetails(item.employeeId);
    const fromDetails = nameFromEmployeeDetails(details);
    if (fromDetails) return fromDetails;
  } catch {
    // Fall through to username when details are unavailable.
  }
  const username = item.username?.trim() ?? '';
  if (username && !looksLikeEmployeeNumber(username, item.employeeNumber)) {
    return username;
  }
  return null;
}

function fromDetails(item: TeamDetails): FormState {
  return {
    teamCode: item.teamCode,
    teamName: item.teamName,
    description: item.description ?? '',
    departmentId: item.departmentId,
    departmentLabel: item.departmentName,
    locationId: item.locationId,
    locationLabel: item.locationName,
    teamLeaderId: item.teamLeaderId,
    teamLeaderLabel: item.teamLeadName,
    isActive: item.isActive !== false,
  };
}

export function validateTeamForm(
  form: FormState,
  isEdit: boolean,
): string | null {
  if (!isEdit && !form.teamCode.trim()) return 'Team code is required.';
  if (!form.teamName.trim()) return 'Team name is required.';
  if (!form.departmentId || form.departmentId <= 0) {
    return 'Department is required.';
  }
  if (!form.teamLeaderId || form.teamLeaderId <= 0) {
    return 'Team lead is required.';
  }
  return null;
}

function isEditRoute(props: Props): props is EditProps {
  return props.route.name === 'TeamEdit';
}

export function TeamFormScreen(props: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      readOnlyLabel: {
        ...typography.caption,
        color: palette.textSecondary,
        fontWeight: '600' as const,
      },
      readOnlyValue: { ...typography.body, color: palette.textPrimary },
      error: { ...typography.caption, color: palette.error },
      switchLabel: { ...typography.body, color: palette.textPrimary },
      help: { ...typography.caption, color: palette.textMuted },
    }),
    [palette],
  );

  const isEdit = isEditRoute(props);
  const teamId = isEdit ? props.route.params.teamId : null;
  const navigation = props.navigation;
  const auth = useAuth();
  const canSelectTeamLead = auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [details, setDetails] = useState<TeamDetails | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const dirty = useRef(false);
  const savingLock = useRef(false);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const data = await getTeamDetails(teamId);
      setDetails(data);
      setForm(fromDetails(data));
      dirty.current = false;
      setError(null);
    } catch (caught) {
      setError(
        organizationErrorMessage(
          caught,
          'The requested record could not be loaded.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (isEdit) load().catch(() => undefined);
  }, [isEdit, load]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    dirty.current = true;
    setForm(current => ({ ...current, [key]: value }));
  }

  function confirmLeave() {
    if (!dirty.current) {
      navigation.goBack();
      return;
    }
    Alert.alert(
      'Discard changes?',
      'You have unsaved changes. Leave without saving?',
      [
        { text: 'Stay', style: 'cancel' },
        {
          text: 'Discard',
          style: 'destructive',
          onPress: () => navigation.goBack(),
        },
      ],
    );
  }

  const loadDepartmentOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getDepartments({
        page: 1,
        pageSize: 20,
        filters: activeConfigurationFilters({ search: search || undefined }),
      });
      return result.items.map(item => ({
        id: item.departmentId,
        label: item.departmentName,
        subtitle: item.departmentCode,
      }));
    },
    [],
  );

  const loadLocationOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const result = await getLocations({
        page: 1,
        pageSize: 20,
        filters: activeConfigurationFilters({ search: search || undefined }),
      });
      return result.items.map(item => ({
        id: item.locationId,
        label: item.locationName,
        subtitle: item.locationCode,
      }));
    },
    [],
  );

  const loadEmployeeOptions = useCallback(
    async (search: string): Promise<SelectorOption[]> => {
      const query = search.trim();
      if (query.length < 3) return [];

      const result = await getEmployees({
        page: 1,
        pageSize: 20,
        filters: { search: query },
      });
      const options = await Promise.all(
        result.items.map(async item => {
          const label = await teamLeadLabel(item);
          return label ? { id: item.employeeId, label } : null;
        }),
      );
      return options.filter((option): option is SelectorOption => option != null);
    },
    [],
  );

  async function submit() {
    if (savingLock.current) return;
    const validation = validateTeamForm(form, isEdit);
    if (validation) {
      setFieldError(validation);
      return;
    }

    savingLock.current = true;
    setSaving(true);
    setFieldError(null);
    setError(null);

    const departmentId = form.departmentId as number;

    try {
      if (isEdit && teamId && details) {
        await runOperation('update', () => updateTeam(teamId, {
          departmentId,
          locationId: form.locationId,
          teamLeaderId: form.teamLeaderId,
          teamName: form.teamName,
          description: form.description,
          displayOrder: details.displayOrder ?? 0,
          isActive: form.isActive,
          rowVersion: details.rowVersion,
        }));
      } else {
        await runOperation('add', () => createTeam({
          departmentId,
          locationId: form.locationId,
          teamLeaderId: form.teamLeaderId,
          teamCode: form.teamCode,
          teamName: form.teamName,
          description: form.description,
        }));
      }
      dirty.current = false;
      navigation.goBack();
    } catch (caught) {
      if (isConcurrencyConflict(caught) && isEdit) {
        const message = organizationErrorMessage(caught);
        await load();
        setError(message);
      } else {
        setError(
          organizationErrorMessage(
            caught,
            isEdit ? 'Unable to update team.' : 'Unable to create team.',
          ),
        );
      }
    } finally {
      savingLock.current = false;
      setSaving(false);
    }
  }

  const permission = isEdit
    ? PERMISSIONS.TEAM_UPDATE
    : PERMISSIONS.TEAM_CREATE;

  return (
    <PermissionGuard permission={permission} fallback={<UnauthorizedScreen />}>
      {loading ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title={isEdit ? 'Edit team' : 'Create team'}
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading team" />
        </AppScreen>
      ) : error && isEdit && !details ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit team"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : (
        <AppScreen webLayout="narrow" scroll keyboardSafe>
          <ScreenHeader
            title={isEdit ? 'Edit team' : 'Create team'}
            onBack={navigation.goBack}
          />

          <View style={styles.body}>
          <AppCard style={styles.card}>
            {isEdit ? (
              <>
                <Text style={themed.readOnlyLabel}>Team code</Text>
                <Text style={themed.readOnlyValue}>{form.teamCode}</Text>
              </>
            ) : (
              <AppInput
                label="Team code *"
                value={form.teamCode}
                onChangeText={value => setField('teamCode', value)}
                autoCapitalize="characters"
              />
            )}
            <AppInput
              label="Team name *"
              value={form.teamName}
              onChangeText={value => setField('teamName', value)}
            />
            <AppInput
              label="Description"
              value={form.description}
              onChangeText={value => setField('description', value)}
            />
            <SearchableEntitySelector
              label="Department *"
              selectedValue={form.departmentId}
              selectedLabel={form.departmentLabel}
              loadOptions={loadDepartmentOptions}
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  departmentId: option.id,
                  departmentLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  departmentId: null,
                  departmentLabel: null,
                }));
              }}
              placeholder="Search departments"
            />
            <SearchableEntitySelector
              label="Location"
              selectedValue={form.locationId}
              selectedLabel={form.locationLabel}
              loadOptions={loadLocationOptions}
              onSelect={option => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  locationId: option.id,
                  locationLabel: option.label,
                }));
              }}
              onClear={() => {
                dirty.current = true;
                setForm(current => ({
                  ...current,
                  locationId: null,
                  locationLabel: null,
                }));
              }}
              placeholder="Search locations"
            />
            {canSelectTeamLead ? (
              <SearchableEntitySelector
                label="Team lead *"
                selectedValue={form.teamLeaderId}
                selectedLabel={form.teamLeaderLabel}
                loadOptions={loadEmployeeOptions}
                minSearchLength={3}
                onSelect={option => {
                  dirty.current = true;
                  setForm(current => ({
                    ...current,
                    teamLeaderId: option.id,
                    teamLeaderLabel: option.label,
                  }));
                }}
                onClear={() => {
                  dirty.current = true;
                  setForm(current => ({
                    ...current,
                    teamLeaderId: null,
                    teamLeaderLabel: null,
                  }));
                }}
                placeholder="Enter at least 3 characters of the employee name"
              />
            ) : (
              <Text style={themed.help}>
                Team lead selection requires employee view permission.
                {form.teamLeaderLabel
                  ? ` Current lead: ${form.teamLeaderLabel}`
                  : ''}
              </Text>
            )}
            {isEdit ? (
              <View style={styles.switchRow}>
                <Text style={themed.switchLabel}>Active</Text>
                <Switch
                  value={form.isActive}
                  onValueChange={value => setField('isActive', value)}
                />
              </View>
            ) : null}
            {fieldError ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {fieldError}
              </Text>
            ) : null}
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <AppButton title="Save" onPress={submit} loading={saving} />
            <AppButton
              title="Cancel"
              variant="secondary"
              onPress={confirmLeave}
            />
          </AppCard>
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  card: { gap: spacing.lg },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});
